// Convert Slate-JSON value (as stored in form fields by SlateRichTextEditor)
// into HTML for read-only rendering with components like ContentWithReadMore.

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const serializeNode = (node: any): string => {
  if (typeof node?.text === 'string') {
    let text = escapeHtml(node.text);
    if (node.code) text = `<code>${text}</code>`;
    if (node.bold) text = `<strong>${text}</strong>`;
    if (node.italic) text = `<em>${text}</em>`;
    if (node.underline) text = `<u>${text}</u>`;
    if (node.strikethrough) text = `<s>${text}</s>`;
    return text;
  }

  const children = Array.isArray(node?.children)
    ? node.children.map(serializeNode).join('')
    : '';
  const align = node?.align;
  const style = align ? ` style="text-align: ${align}"` : '';

  switch (node?.type) {
    case 'block-quote':
      return `<blockquote${style}>${children}</blockquote>`;
    case 'heading-one':
      return `<h1${style}>${children}</h1>`;
    case 'heading-two':
      return `<h2${style}>${children}</h2>`;
    case 'heading-three':
      return `<h3${style}>${children}</h3>`;
    case 'heading-four':
      return `<h4${style}>${children}</h4>`;
    case 'heading-five':
      return `<h5${style}>${children}</h5>`;
    case 'heading-six':
      return `<h6${style}>${children}</h6>`;
    case 'bulleted-list':
      return `<ul>${children}</ul>`;
    case 'numbered-list':
      return `<ol>${children}</ol>`;
    case 'list-item':
      return `<li>${children}</li>`;
    case 'link':
      return `<a href="${escapeHtml(node.url || '')}" target="_blank" rel="noopener noreferrer">${children}</a>`;
    case 'paragraph':
    default:
      return `<p${style}>${children}</p>`;
  }
};

export const slateJsonToHtml = (value: string): string => {
  if (!value) return '';
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.map(serializeNode).join('');
    }
  } catch {
    // not Slate JSON — assume plain text
  }
  return `<p>${escapeHtml(value).replace(/\n/g, '<br />')}</p>`;
};

const collectText = (nodes: any[]): string =>
  nodes
    .map((n) => {
      if (typeof n?.text === 'string') return n.text;
      if (Array.isArray(n?.children)) return collectText(n.children);
      return '';
    })
    .join(' ');

export const slateJsonToText = (value: string): string => {
  if (!value) return '';
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return collectText(parsed).replace(/\s+/g, ' ').trim();
    }
  } catch {
    // not Slate JSON — assume plain text
  }
  return value;
};
